import { Link, createFileRoute } from '@tanstack/react-router';

import Button from '../../components/Button';
import Contact from '../../components/Contact';
import FAQ from '../../components/FAQ';
import Hero from '../../components/Hero';
import Horaires from '../../components/Horaires';
import Map from '../../components/Map';
import ScrollTopButton from '../../components/ScrollTopButton';
import TattooGallery from '../../components/Tattoo/TattooGallery/TattooGallery';
import { contentQueries, useGetContent } from '../../lib/react-query/ContentQueries';
import { heroQueries } from '../../lib/react-query/HeroQueries';
import { tattooQueries, useGetAllTattooImages } from '../../lib/react-query/TattooQueries';

export const Route = createFileRoute('/tatouage/')({
  component: TatouagePage,
  loader: async ({ context }) => {
    const { queryClient } = context;

    // Preload content, hero, and tattoo images in parallel
    const [, hero] = await Promise.all([
      queryClient.ensureQueryData(contentQueries.main()),
      queryClient.ensureQueryData(heroQueries.byType('tattoo')),
      queryClient.ensureQueryData(tattooQueries.all()),
    ]);

    const shuffledHeroImages = hero.images
      ? [...hero.images].sort(() => Math.random() - 0.5).slice(0, 5)
      : [];

    return { shuffledHeroImages };
  },
  head: () => ({
    meta: [
      {
        title: 'Tatouage | Azelysse Piercing & Tattoo',
      },
      {
        name: 'description',
        content:
          "Tatouage sur-mesure par Azelysse à Égly (91), Essonne. Chaque création est unique. Proche d'Arpajon, Brétigny-sur-Orge, Montlhéry, Sainte-Geneviève-des-Bois. Galerie et projet personnalisé.",
      },
      {
        property: 'og:title',
        content: 'Tatouage | Azelysse Piercing & Tattoo',
      },
      {
        property: 'og:description',
        content:
          "Tatouage sur-mesure par Azelysse à Égly (91). Chaque création est unique. Proche d'Arpajon, Brétigny, Montlhéry.",
      },
      {
        property: 'og:url',
        content: 'https://azelysse.fr/tatouage',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://azelysse.fr/tatouage' }],
  }),
});

function TatouagePage() {
  const { shuffledHeroImages } = Route.useLoaderData();
  const { data: tattooImages = [] } = useGetAllTattooImages();
  const { data: content } = useGetContent();

  const hasGoogleCoordinate =
    content?.google?.maps_coordinate_lat && content.google.maps_coordinate_lng;

  return (
    <div className="min-h-screen bg-emerald-50">
      <svg
        className="absolute inset-0 h-full w-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-black/10"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="tatouage-pattern"
            width={200}
            height={200}
            x="50%"
            y={-1}
            patternUnits="userSpaceOnUse"
          >
            <path d="M.5 200V.5H200" fill="none" />
          </pattern>
        </defs>
        <svg x="50%" y={-1} className="overflow-visible fill-emerald-100/20">
          <path
            d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
            strokeWidth={0}
          />
        </svg>
        <rect width="100%" height="100%" strokeWidth={0} fill="url(#tatouage-pattern)" />
      </svg>
      <Hero type="tattoo" images={shuffledHeroImages} />

      {tattooImages.length > 0 && (
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <h2 className="mb-8 text-3xl font-bold tracking-tight text-gray-900">
            Galerie de tatouages
          </h2>
          <TattooGallery images={tattooImages} />
        </div>
      )}

      {/* CTA Section - Project Discussion */}
      <div className="relative isolate mt-4 w-full transform overflow-hidden border bg-emerald-900 py-4">
        {/* Content */}
        <div className="relative">
          <div className="mx-auto flex max-w-7xl justify-center px-6 py-16 lg:px-8">
            <div className="mx-auto lg:max-w-none">
              <div className="max-w-xl lg:max-w-lg">
                <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  Discutons de votre projet
                </h2>
                <p className="mt-4 text-lg leading-8 text-emerald-200">
                  Chaque tatouage est unique. Prenons le temps d'échanger sur vos idées, vos
                  inspirations et créons ensemble un design qui vous ressemble.
                </p>
                <div className="mt-4">
                  <Link to="/tatouage/projet">
                    <Button
                      size="xl"
                      trackEvent="RendezVousClick"
                      trackProps={{ location: 'tatouage-cta' }}
                      className="bg-emerald-500 hover:bg-emerald-400"
                    >
                      Discutez de votre projet
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Background gradient blur */}
          <div
            className="absolute top-0 left-1/2 -z-10 -translate-x-1/2 blur-3xl xl:-top-6"
            aria-hidden="true"
          >
            <div
              className="aspect-1155/678 w-288.75 bg-linear-to-tr from-emerald-400 to-emerald-600 opacity-30"
              style={{
                clipPath:
                  'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
              }}
            />
          </div>
        </div>
      </div>

      <div
        id="informations"
        className="mx-auto max-w-7xl px-6 pt-36 pb-32 sm:pt-60 lg:px-8 lg:pt-32"
      >
        <div className="grid grid-cols-1 gap-16 md:grid-cols-3">
          {hasGoogleCoordinate ? (
            <>
              <div className="md:col-span-2">
                <Map height="h-[500px] md:h-full" />
              </div>
              <Horaires type="tattoo" />
            </>
          ) : (
            <div className="md:col-span-3">
              <Horaires type="tattoo" />
            </div>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact">
        <Contact />
      </div>

      {/* FAQ Section */}
      <div id="faq">
        <FAQ type="tattoo" theme="emerald" />
      </div>

      <ScrollTopButton />
    </div>
  );
}
